/**
 * Animates the search input placeholder: types a word out, deletes it letter by
 * letter, then moves on to the next word and loops.
 *
 * Driven by data attributes rendered in snippets/search-bar.liquid:
 *   data-placeholder-prefix  - text kept in front of every word
 *   data-placeholder-words   - JSON array of words to cycle through
 */
(function () {
  const TYPE_SPEED = 90; // ms per typed letter
  const DELETE_SPEED = 45; // ms per removed letter
  const HOLD_FULL = 1600; // pause on the complete word
  const HOLD_EMPTY = 350; // pause before the next word starts
  const HIDDEN_RETRY = 800; // re-check delay while the input is not on screen

  class PlaceholderTypewriter {
    constructor(input) {
      this.input = input;
      this.prefix = input.dataset.placeholderPrefix || '';

      try {
        this.words = JSON.parse(input.dataset.placeholderWords || '[]');
      } catch (error) {
        this.words = [];
      }
      this.words = this.words.filter((word) => typeof word === 'string' && word.length > 0);

      if (!this.words.length) return;

      // Start on the full first word (the placeholder is already rendered that
      // way server side) and delete from there, so nothing jumps on load.
      this.wordIndex = 0;
      this.charIndex = this.words[0].length;
      this.deleting = true;
      this.timer = null;

      this.render();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.step = this.step.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onReset = this.onReset.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('search', this.onInput);
      // A form reset empties the field without firing an input event.
      this.input.form?.addEventListener('reset', this.onReset);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.schedule(HOLD_FULL);
    }

    render() {
      this.input.setAttribute('placeholder', this.prefix + this.words[this.wordIndex].slice(0, this.charIndex));
    }

    schedule(delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.step, delay);
    }

    stop() {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Nothing to animate while the user is typing or the field is off screen.
    isPaused() {
      return this.input.value.length > 0 || document.hidden || this.input.offsetParent === null;
    }

    step() {
      if (this.isPaused()) {
        this.schedule(HIDDEN_RETRY);
        return;
      }

      const word = this.words[this.wordIndex];
      this.charIndex += this.deleting ? -1 : 1;
      this.render();

      let delay = this.deleting ? DELETE_SPEED : TYPE_SPEED;

      if (!this.deleting && this.charIndex >= word.length) {
        this.deleting = true;
        delay = HOLD_FULL;
      } else if (this.deleting && this.charIndex <= 0) {
        this.deleting = false;
        this.wordIndex = (this.wordIndex + 1) % this.words.length;
        delay = HOLD_EMPTY;
      }

      this.schedule(delay);
    }

    onInput() {
      if (this.input.value.length) {
        this.stop();
      } else if (!this.timer) {
        this.schedule(HOLD_EMPTY);
      }
    }

    onReset() {
      // The value is still present while the reset event runs.
      setTimeout(this.onInput);
    }

    onVisibilityChange() {
      if (!document.hidden && !this.timer && !this.input.value.length) {
        this.schedule(HOLD_EMPTY);
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-placeholder-words]').forEach((input) => {
      if (input.dataset.placeholderTypewriter === 'ready') return;
      input.dataset.placeholderTypewriter = 'ready';
      new PlaceholderTypewriter(input);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The header can be re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
